// third-party
import { FormattedMessage } from 'react-intl';

// assets
import {
    Article as ArticleIcon,
    AssuredWorkload,
    AutoAwesome,
    Balance as BalanceIcon,
    BarChart,
    BoxPlotOutlined,
    BusinessOutlined as BusinessOutlinedIcon,
    GroupsRounded,
    HelpOutline,
    Home as HomeIcon,
    MonetizationOn as MonetizationOnIcon,
    Percent,
    ReceiptLong as ReceiptLongIcon,
    SchoolOutlined,
    SettingFilled,
    SmartToy as SmartToyIcon,
    StopOutlined,
    SwapHorizontalCircle,
    Task,
    TeamOutlined,
    UploadFileRounded,
    UserOutlined,
    WorkOutlineOutlined,
    WorkRounded,
} from 'components/icons';
import ArrowTransferRectangle from 'assets/images/icons/ArrowTransferRectangle.svg?react';
import Compliance from 'assets/images/icons/Compliance.svg?react';

// type
import { REPORT_ACTIVE_URLS, REPORT_PERMISSIONS } from 'pages/reporting/reportCatalog';
import TimecardNavIcon from 'pages/timecards/TimecardNavIcon';
import { ENV } from 'types/env';
import { FeatureFlag } from 'types/features';
import { NavItemType } from 'types/menu';
import { currentEnv, isLocalEnv } from 'utils/env';

// icons
const icons = {
    UserOutlined,
    BusinessOutlinedIcon,
    StopOutlined,
    AssuredWorkload,
    AutoAwesome,
    TeamOutlined,
    ArticleIcon,
    GroupsRounded,
    WorkOutlineOutlined,
    Percent,
    BalanceIcon,
    BoxPlotOutlined,
    SettingFilled,
    ReceiptLongIcon,
    SchoolOutlined,
    MonetizationOnIcon,
    HomeIcon,
    WorkRounded,
    UploadFileRounded,
    BarChart,
    ArrowTransferRectangle,
    Task,
    Compliance,
    SwapHorizontalCircle,
    SmartToyIcon,
};

const AiChatIcon = ({ style, ...props }: React.ComponentProps<typeof AutoAwesome>) => (
    <AutoAwesome {...props} style={{ ...style, color: '#9999FF' }} />
);

const reportingLinks = {
    [ENV.DEMO]: 'https://reporting.demo.crespect.com',
    [ENV.PRELIVE]: 'https://reporting.prelive.crespect.com',
    [ENV.TEST]: 'https://reporting.test.crespect.com',
    [ENV.PROD]: 'https://reporting.crespect.com',
    [ENV.WARDYNSKI]: 'https://reporting.wardynski.crespect.com',
    [ENV.DEV]: 'https://reporting.dev.crespect.com',
    [ENV.LOCAL]: 'https://reporting.dev.crespect.com',
};

// ==============================|| MENU ITEMS - SUPPORT ||============================== //

const getItems = (): NavItemType => {
    const reportingLink = currentEnv !== undefined && !isLocalEnv ? reportingLinks[currentEnv] : undefined;
    return {
        id: 'other',
        type: 'group',
        children: [
            {
                id: 'dashboard',
                title: <FormattedMessage id="menu.dashboard" />,
                icon: icons.HomeIcon,
                type: 'collapse',
                url: '/home',
                showChildrenOnlyOnPopper: true,
                children: [
                    {
                        id: 'dashboard',
                        title: <FormattedMessage id="menu.dashboard" />,
                        type: 'item',
                        url: '/home',
                    },
                ],
            },
            {
                id: 'ai-chat',
                title: <FormattedMessage id="ai-chat.title" />,
                icon: AiChatIcon,
                type: 'collapse',
                url: '/ai-chat',
                showChildrenOnlyOnPopper: true,
                requiredFeature: FeatureFlag.AI_CHAT_ENABLED,
                permissions: ['READ.CRESPECT_AI'],
                children: [
                    {
                        id: 'ai-chat-page',
                        title: <FormattedMessage id="ai-chat.title" />,
                        type: 'item',
                        url: '/ai-chat',
                    },
                ],
            },
            {
                id: 'contact-database',
                title: <FormattedMessage id="menu.contacts" />,
                icon: icons.TeamOutlined,
                type: 'collapse',
                children: [
                    {
                        id: 'contacts',
                        title: <FormattedMessage id="contacts.contacts" />,
                        type: 'item',
                        url: '/contacts',
                        altActiveUrls: ['/contact'],
                        disabled: false,
                    },
                    {
                        id: 'groups',
                        title: <FormattedMessage id="menu.groups" />,
                        type: 'item',
                        url: '/groups',
                        altActiveUrls: ['/group'],
                        disabled: false,
                    },
                    {
                        id: 'my-portfolio',
                        title: <FormattedMessage id="menu.my-portfolio" />,
                        type: 'item',
                        url: '/my-portfolio',
                        permissions: [
                            'READ.PARTIES.ALL',
                            'READ.PARTIES.MY_CONTACTS',
                            'READ.PARTIES.RELATED_TO_MY_CASES',
                        ],
                        requiredFeature: FeatureFlag.MY_PORTFOLIO,
                    },
                    {
                        id: 'contracts',
                        title: <FormattedMessage id="contracts.contracts" />,
                        type: 'item',
                        url: '/contracts',
                        altActiveUrls: ['/contract'],
                        // icon: icons.ArticleIcon,
                        disabled: false,
                    },
                    {
                        id: 'marketing-profiles',
                        title: <FormattedMessage id="marketing-profiles.profiles" />,
                        type: 'item',
                        url: '/contacts/marketing',
                        requiredFeature: FeatureFlag.MARKETING_PROFILE_PAGE,
                        permissions: ['READ.MARKETING.ALL'],
                    },
                ],
            },
            {
                id: 'cases-database',
                title: <FormattedMessage id="matters.matters" />,
                type: 'collapse',
                altActiveUrls: ['/matter', '/new-cases'],
                icon: icons.BalanceIcon,
                children: [
                    {
                        id: 'cases',
                        title: <FormattedMessage id="matters.matters" />,
                        type: 'item',
                        url: '/matters',
                        altActiveUrls: ['/matter', '/new-cases'],
                    },
                    {
                        id: 'projects',
                        title: <FormattedMessage id="projects.projects" />,
                        type: 'item',
                        url: '/projects',
                        altActiveUrls: ['/project'],
                    },
                    {
                        id: 'delegations',
                        title: <FormattedMessage id="menu.delegations" />,
                        type: 'item',
                        url: '/delegations',
                        altActiveUrls: ['/delegation'],
                        requiredFeature: FeatureFlag.DELEGATION,
                        permissions: [
                            'VIEW.DELEGATIONS.MY_OWN',
                            'VIEW.DELEGATIONS.MY_TEAM',
                            'VIEW.DELEGATIONS.MY_CORE_TEAM',
                            'VIEW.DELEGATIONS.ALL',
                        ],
                    },
                ],
            },
            {
                id: 'timecards',
                title: <FormattedMessage id="timecards.timecards" />,
                type: 'collapse',
                url: '/timecards',
                icon: TimecardNavIcon,
                showChildrenOnlyOnPopper: true,
                children: [
                    {
                        id: 'timecards',
                        title: <FormattedMessage id="timecards.timecards" />,
                        type: 'item',
                        url: '/timecards',
                    },
                ],
            },
            {
                id: 'bd',
                title: <FormattedMessage id="menu.bd" />,
                icon: icons.WorkRounded,
                type: 'collapse',
                style: { fontSize: '0.8125rem' },
                children: [
                    {
                        id: 'efforts',
                        title: <FormattedMessage id="menu.efforts" />,
                        type: 'item',
                        url: '/efforts',
                    },
                    {
                        id: 'experience-records',
                        title: <FormattedMessage id="experience-records.experience-records" />,
                        type: 'item',
                        url: '/experience-records',
                    },
                    {
                        id: 'law-firms',
                        title: <FormattedMessage id="menu.law-firms-hub" />,
                        type: 'item',
                        url: '/law-firms',
                        requiredFeature: FeatureFlag.GLOBAL_LAW_FIRMS_HUB,
                    },
                    {
                        id: 'rankings-referees',
                        title: <FormattedMessage id="menu.rankings-referees" />,
                        type: 'item',
                        url: '/rankings-referees',
                        requiredFeature: FeatureFlag.RANKINGS_REFEREE,
                    },
                ],
            },
            {
                id: 'tasks',
                title: <FormattedMessage id="menu.tasks" />,
                icon: icons.Task,
                type: 'collapse',
                url: '/tasks',
                showChildrenOnlyOnPopper: true,
                children: [
                    {
                        id: 'tasks',
                        title: <FormattedMessage id="menu.tasks" />,
                        type: 'item',
                        url: '/tasks',
                    },
                ],
            },
            {
                id: 'correspondence',
                title: <FormattedMessage id="menu.correspondence" />,
                icon: icons.SwapHorizontalCircle,
                type: 'collapse',
                url: `/correspondence`,
                showChildrenOnlyOnPopper: true,
                requiredFeature: FeatureFlag.CORRESPONDENCE_MODULE,
                children: [
                    {
                        id: 'correspondence',
                        title: <FormattedMessage id="menu.correspondence" />,
                        type: 'item',
                        url: `/correspondence`,
                    },
                ],
            },
            {
                id: 'debriefings',
                title: <FormattedMessage id="menu.debriefings" />,
                type: 'collapse',
                icon: icons.GroupsRounded,
                url: '/debriefings',
                showChildrenOnlyOnPopper: true,
                requiredFeature: FeatureFlag.DEBRIEFING_MODULE,
                permissions: ['READ.DEBRIEFINGS.ALL'],
                children: [
                    {
                        id: 'debriefings',
                        title: <FormattedMessage id="menu.debriefings" />,
                        type: 'item',
                        url: '/debriefings',
                        requiredFeature: FeatureFlag.DEBRIEFING_MODULE,
                        permissions: ['READ.DEBRIEFINGS.ALL'],
                    },
                ],
            },
            {
                id: 'revenue-management-collapse',
                title: <FormattedMessage id="menu.revenue" />,
                type: 'collapse',
                icon: icons.MonetizationOnIcon,
                children: [
                    {
                        id: 'billableclients',
                        title: <FormattedMessage id="menu.billable-clients" />,
                        type: 'item',
                        url: '/billable-clients',
                    },
                    {
                        id: 'invoices',
                        title: <FormattedMessage id="menu.invoices" />,
                        type: 'item',
                        url: '/invoices',
                    },
                    {
                        id: 'prepayments',
                        title: <FormattedMessage id="menu.prepayments" />,
                        type: 'item',
                        url: '/prepayments',
                    },
                    {
                        id: 'expenses',
                        title: <FormattedMessage id="menu.expenses" />,
                        type: 'item',
                        url: '/expenses',
                    },
                    {
                        id: 'pricelists',
                        title: <FormattedMessage id="menu.pricelists" />,
                        type: 'item',
                        url: '/pricelists',
                        // icon: icons.ReceiptLongIcon,
                    },
                    {
                        id: 'banktransfers',
                        title: <FormattedMessage id="menu.bank-transfers" />,
                        type: 'item',
                        url: '/bank-transfers',
                        permissions: ['READ.BANK_TRANSACTIONS.ALL'],
                    },
                    {
                        id: 'client-assets',
                        title: <FormattedMessage id="menu.client-assets" />,
                        type: 'item',
                        url: '/client-assets',
                        altActiveUrls: ['/client-asset'],
                        permissions: ['READ.PARTY_ASSETS'],
                        requiredFeature: FeatureFlag.PARTY_ASSETS,
                    },
                ],
            },
            {
                id: 'compliance',
                title: <FormattedMessage id="compliance.compliance" />,
                type: 'collapse',
                icon: icons.Compliance,
                children: [
                    {
                        id: 'compliance-overview',
                        title: <FormattedMessage id="menu.overview" />,
                        type: 'item',
                        url: '/compliance',
                    },
                    {
                        id: 'coi-checks',
                        title: <FormattedMessage id="menu.coi-check" />,
                        type: 'item',
                        url: '/coi-checks',
                        altActiveUrls: ['/coi-checks'],
                    },
                ],
                permissions: ['READ.PARTIES.ALL', 'READ.PARTIES.MY_CONTACTS', 'READ.PARTIES.RELATED_TO_MY_CASES'],
            },
            {
                id: 'colleagues',
                title: <FormattedMessage id="menu.colleagues" />,
                type: 'collapse',
                icon: icons.UserOutlined,
                url: '/employees',
                showChildrenOnlyOnPopper: true,
                children: [
                    {
                        id: 'colleagues',
                        title: <FormattedMessage id="menu.colleagues" />,
                        type: 'item',
                        url: '/employees',
                        altActiveUrls: ['/employee'],
                    },
                ],
            },
            {
                id: 'trainings',
                title: <FormattedMessage id="menu.trainings" />,
                type: 'collapse',
                icon: icons.SchoolOutlined,
                requiredFeature: FeatureFlag.TRAININGS_MODULE,
                permissions: ['READ.TRAININGS.ALL'],
                children: [
                    {
                        id: 'trainings',
                        title: <FormattedMessage id="menu.trainings" />,
                        type: 'item',
                        url: '/trainings',
                        requiredFeature: FeatureFlag.TRAININGS_MODULE,
                        permissions: ['READ.TRAININGS.ALL'],
                    },
                    {
                        id: 'trainings-reports',
                        title: <FormattedMessage id="menu.trainings-reports" />,
                        type: 'item',
                        url: '/trainings/reports',
                        requiredFeature: FeatureFlag.TRAININGS_MODULE,
                        permissions: ['READ.TRAININGS.ALL'],
                    },
                ],
            },
            {
                id: 'reporting',
                title: <FormattedMessage id="menu.reporting" />,
                icon: icons.BarChart,
                type: 'collapse',
                url: reportingLink,
                target: '_blank',
                showChildrenOnlyOnPopper: true,
                disabled: reportingLink === undefined,
                permissions: ['READ.REPORTS.BASIC', 'READ.REPORTS.ALL'],
                requiredFeature: FeatureFlag.REPORTS_MODULE,
                children: [
                    {
                        id: 'reporting',
                        title: <FormattedMessage id="menu.reporting" />,
                        type: 'item',
                        url: reportingLink,
                        target: '_blank',
                        requiredFeature: FeatureFlag.REPORTS_MODULE,
                    },
                ],
            },
            {
                id: 'reports',
                title: <FormattedMessage id="reporting.title" />,
                type: 'collapse',
                icon: icons.BarChart,
                url: '/reports',
                altActiveUrls: REPORT_ACTIVE_URLS,
                showChildrenOnlyOnPopper: true,
                permissions: REPORT_PERMISSIONS,
                requiredFeature: FeatureFlag.PORTAL_REPORTS,
                children: [
                    {
                        id: 'reports',
                        title: <FormattedMessage id="reporting.title" />,
                        type: 'item',
                        url: '/reports',
                        altActiveUrls: REPORT_ACTIVE_URLS,
                        permissions: REPORT_PERMISSIONS,
                        requiredFeature: FeatureFlag.PORTAL_REPORTS,
                    },
                ],
            },
            {
                id: 'accounting-reports',
                title: <FormattedMessage id="menu.accounting-reports" />,
                type: 'collapse',
                icon: icons.BarChart,
                permissions: ['READ.ACCOUNTING_REPORTS'],
                requiredFeature: FeatureFlag.ACCOUNTING_REPORTS,
                children: [
                    {
                        id: 'open-advances',
                        title: <FormattedMessage id="menu.open-advances" />,
                        type: 'item',
                        url: '/reports-open-advances',
                        permissions: ['READ.ACCOUNTING_REPORTS'],
                        requiredFeature: FeatureFlag.ACCOUNTING_REPORTS,
                    },
                    {
                        id: 'open-invoices',
                        title: <FormattedMessage id="menu.open-invoices" />,
                        type: 'item',
                        url: '/reports-open-invoices',
                        permissions: ['READ.ACCOUNTING_REPORTS'],
                        requiredFeature: FeatureFlag.ACCOUNTING_REPORTS,
                    },
                    {
                        id: 'unpaid-invoices',
                        title: <FormattedMessage id="menu.unpaid-invoices" />,
                        type: 'item',
                        url: '/reports-unpaid-invoices',
                        permissions: ['READ.ACCOUNTING_REPORTS'],
                        requiredFeature: FeatureFlag.ACCOUNTING_REPORTS,
                    },
                    {
                        id: 'sales-journal',
                        title: <FormattedMessage id="menu.sales-journal" />,
                        type: 'item',
                        url: '/reports-sales-journal',
                        permissions: ['READ.ACCOUNTING_REPORTS'],
                        requiredFeature: FeatureFlag.ACCOUNTING_REPORTS,
                    },
                    {
                        id: 'foreign-sales',
                        title: <FormattedMessage id="menu.foreign-sales" />,
                        type: 'item',
                        url: '/reports-foreign-sales',
                        permissions: ['READ.ACCOUNTING_REPORTS'],
                        requiredFeature: FeatureFlag.ACCOUNTING_REPORTS,
                    },
                ],
            },
            {
                id: 'ndf',
                title: <FormattedMessage id="menu.ndf-export" />,
                type: 'collapse',
                icon: icons.UploadFileRounded,
                permissions: ['READ.NDF'],
                requiredFeature: FeatureFlag.NDF_EXPORT,
                children: [
                    {
                        id: 'hbr',
                        title: <FormattedMessage id="menu.payroll-hbr" />,
                        type: 'item',
                        url: '/ndf-export-hbr',
                        permissions: ['READ.NDF'],
                        requiredFeature: FeatureFlag.NDF_EXPORT,
                    },
                    {
                        id: 'erp',
                        title: <FormattedMessage id="menu.sunsystems-erp" />,
                        type: 'item',
                        url: '/ndf-export-erp',
                        permissions: ['READ.NDF'],
                        requiredFeature: FeatureFlag.NDF_EXPORT,
                    },
                ],
            },
            {
                id: 'admin',
                title: <FormattedMessage id="menu.backoffice" />,
                type: 'collapse',
                icon: icons.SettingFilled,
                children: [
                    {
                        id: 'offices',
                        title: <FormattedMessage id="offices.offices" />,
                        type: 'item',
                        url: '/offices',
                        altActiveUrls: ['/office'],
                        // icon: icons.BusinessOutlinedIcon,
                    },
                    {
                        id: 'practice-areas',
                        title: <FormattedMessage id="menu.practice-areas" />,
                        type: 'item',
                        url: '/practice-areas',
                        altActiveUrls: ['/practice-area'],
                        // icon: icons.AssuredWorkload,
                    },
                    {
                        id: 'job-roles',
                        title: <FormattedMessage id="menu.job-roles" />,
                        type: 'item',
                        url: '/job-roles',
                        altActiveUrls: ['/job-role'],
                    },
                    {
                        id: 'job-positions',
                        title: <FormattedMessage id="menu.job-positions" />,
                        type: 'item',
                        url: '/job-positions',
                        altActiveUrls: ['/job-position'],
                    },
                    {
                        id: 'teams',
                        title: <FormattedMessage id="teams.teams" />,
                        type: 'item',
                        url: '/teams',
                        altActiveUrls: ['/team'],
                        // icon: icons.TeamOutlined,
                    },
                    {
                        id: 'financial-conditions',
                        title: <FormattedMessage id="menu.financial-conditions" />,
                        type: 'item',
                        url: '/financial-conditions',
                        // icon: icons.Percent,
                    },
                    {
                        id: 'country-management',
                        title: <FormattedMessage id="menu.country-management" />,
                        type: 'item',
                        url: '/country-management',
                        permissions: ['READ.BACK_OFFICE_COUNTRY_MANAGEMENT'],
                    },
                    {
                        id: 'settings',
                        title: <FormattedMessage id="menu.settings" />,
                        type: 'item',
                        url: '/settings',
                        permissions: ['READ.BACK_OFFICE_SETTINGS'],
                        // icon: icons.Percent,
                    },
                    {
                        id: 'audit-trail',
                        title: <FormattedMessage id="audit-trail.title" />,
                        type: 'item',
                        url: '/audit-trail',
                        permissions: ['READ.AUDIT_LOGS'],
                    },
                ],
            },
            {
                id: 'help-center',
                title: <FormattedMessage id="help-center.title" />,
                type: 'item',
                icon: HelpOutline,
                url: '/help-center',
                altActiveUrls: ['/help-center'],
            },
        ],
    };
};

export default getItems;
